import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    DialogContent,
    DialogTitle,
    DialogContentText,
    DialogActions,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    ListItem,
    ListItemText,
    RadioGroup,
    Radio,
    TextField,
    Typography
} from "@mui/material/";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import styles, { getTextStyle } from "./styles";
import debounce from "lodash/debounce";
import CloseIcon from "@mui/icons-material/Close";
import { enums, helpers } from "../../../../../common/lib";
import { strings } from "../../../../../common/i18n";
import { useDispatch, useSelector } from "react-redux";
import { useExclusionDatePicker } from "../../../../../common/hooks";
import { logToSentry } from "../../../../../config/Sentry";
import actions from "../../../../../Store/actions";
import dayjs from "dayjs";

const debounceMs = 300;

export default ({ setLoading, selectedAssets, onClose }) => {
    const { auth, auditsList, auditAssets, typesList, assets } = useSelector(
        (state) => state
    );

    const dispatch = useDispatch();
    const { exclusionReasonTypes } = typesList;

    const [exclusionReason, setExclusionReason] = useState(null);
    const [note, setNote] = useState("");
    const [identifiers, setIdentifiers] = useState("");
    const [activeItem, setActiveItem] = useState("selected");
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [foundList, setFoundList] = useState([]);
    const [validList, setValidList] = useState({
        total: "",
        known: "",
        unknown: ""
    });
    const [dateNotRequired, setDateNotRequired] = useState(false);
    const [disableDatePicker, setDisableDatePicker] = useState(false);

    /** Check if vin is present in the list use assets from store */
    const vinCheck = (vin) =>
        assets.data.filter((item) => item.identifier1 === vin);

    /** Check if the exclusionReason id is sold  */
    const { assetIsSold, assetDeliveredToCustomerAndNotPaid } =
        enums.status.exclusionReason;

    const isExclusionDatePicker = useExclusionDatePicker(exclusionReason);

    /** On disable  */
    /** Correcting validation for empty Submission Issue Fix  */
    const isDisabled = useMemo(() => {
        const date = new Date(selectedDate);
        const currentDate = new Date();
        /** Check condition for both multiple
         * vins and selected assets */
        const checkCommonValidator = () => {
            if (!isExclusionDatePicker) return false;
            if (
                isExclusionDatePicker &&
                parseInt(exclusionReason) ===
                    assetDeliveredToCustomerAndNotPaid &&
                dateNotRequired
            )
                return false;
            if (
                isExclusionDatePicker &&
                parseInt(exclusionReason) ===
                    assetDeliveredToCustomerAndNotPaid &&
                !dateNotRequired &&
                selectedDate
            )
                return false;
            if (
                isExclusionDatePicker &&
                parseInt(exclusionReason) === assetIsSold &&
                date <= currentDate &&
                selectedDate
            )
                return false;
            else return true;
        };

        if (
            activeItem === "selected" &&
            selectedAssets.length > 0 &&
            exclusionReason &&
            note.length > 0
        ) {
            /** if not sold date and all condition are met */
            return checkCommonValidator();
        }
        if (
            activeItem === "enterList" &&
            identifiers.length > 0 &&
            exclusionReason &&
            note
        ) {
            return checkCommonValidator();
        }

        return true;
    }, [
        dateNotRequired,
        disableDatePicker,
        activeItem,
        isExclusionDatePicker,
        selectedAssets,
        exclusionReason,
        note,
        identifiers,
        selectedDate
    ]);

    /** Adding debounce for the vins list check */
    const debouncedSearch = debounce(async (value) => {
        let vinList = [];

        /** split value based on the new line remove empty line if present  */
        const list = value.split(`\n`).filter((item) => item.length > 0);

        let validVin = 0;
        const found = [];
        list.map((item) => {
            const vin = vinCheck(item);
            if (vin.length > 0) {
                validVin++;
                found.push(vin[0]);
            }
            vinList.push(item);
        });
        setFoundList(found);
        setValidList({
            total: vinList.length,
            known: validVin,
            unknown: vinList.length - validVin
        });
    }, debounceMs);

    const onChangeState = useCallback(
        {
            exclusionReason: (val) => setExclusionReason(val),
            identifiers: (val) => {
                setIdentifiers(val);
                debouncedSearch(val);
            },
            date: (val) => {
                setSelectedDate(val);
            },
            note: (val) => setNote(val)
        },
        [debouncedSearch]
    );

    /** On change of identifiers,exclusion and note */
    const onChange = (name) => (event) => {
        try {
            /** Currying for date */
            if (name === "date") onChangeState[name](event);
            else onChangeState[name](event.target.value);

            if (name !== "note") {
                setDateNotRequired(false);
                setDisableDatePicker(false);
            }
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On change edit:", { error });
            dispatch(
                actions.toast.create.error(
                    strings("errors.onChangeEdit", { name })
                )
            );
        }
    };

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, []);

    /** On submitting the assets */
    const onSubmit = async () => {
        try {
            setLoading(true);

            const next = activeItem === "selected" ? selectedAssets : foundList;

            /** Check for the existing tab */
            const isvinListTab = activeItem === "selected" ? false : true;

            /** Get audit Asset id for the assets */
            const getAuditAssetId = (assetId) =>
                auditAssets.data.find((item) => item.asset === assetId).id;

            const currentDate = new Date().toISOString();
            const auditId = auditsList.item.id;
            const assets = [];

            next.map((auditAsset) => {
                assets.push({
                    assetController: {
                        status: enums.status.assetStatus.excluded, // Excluded asset status
                        assignedDate: currentDate,
                        auditAsset: isvinListTab
                            ? getAuditAssetId(auditAsset.id)
                            : auditAsset.id,
                        controller: auth.account.id,
                        exclusionReason: exclusionReason,
                        exclusionReasonDate: isExclusionDatePicker
                            ? selectedDate?.toISOString() || null
                            : null
                    },
                    auditImages: [],
                    auditDocuments: [],
                    auditLocation: auditAsset.auditLocation,
                    notes: [
                        {
                            content: note,
                            createdDate: currentDate
                        }
                    ]
                });
            });

            const submissionData = {
                audit: auditId,
                createdDate: currentDate,
                auditAssetContent: assets
            };

            /** Submission post request previously there was a map function
             * for post request now its replaced  */

            await dispatch(
                actions.auditControllers.create.postRequest(submissionData)
            );
            await dispatch(actions.auditReview.create.getRequest(auditId));
            dispatch(
                actions.toast.create.success(strings("updatedSuccessfully"))
            );

            /** If threre is only 1 unchecked data
             * Then the previous data is processed
             * close the popup
             */
            // if (unCheckedData === 1) onClose && onClose();

            /** Clear all state after submission */
            setExclusionReason(null);
            setIdentifiers("");
            setNote("");
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On exclusion submission:", { error });
            dispatch(
                actions.toast.create.error(
                    strings("errors.onExclusionSubmission")
                )
            );
        }

        setLoading(false);
        onClose();
    };

    const onDisableDateCheckboxChange = (event) => {
        const checked = event.target.checked;
        setDateNotRequired(checked);
        setDisableDatePicker(checked);
        setSelectedDate(checked ? null : dayjs()); // reset or restore today
    };

    return (
        <>
            <DialogTitle>{strings("chooseExclusionReason")}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                <Box sx={styles.containerStyle}>
                    <Typography
                        onClick={() => setActiveItem("selected")}
                        sx={getTextStyle(activeItem === "selected")}
                    >
                        {strings("selected")}
                    </Typography>
                    <Typography
                        onClick={() => setActiveItem("enterList")}
                        sx={getTextStyle(activeItem === "enterList")}
                    >
                        {strings("enterList")}
                    </Typography>
                </Box>

                {activeItem === "selected" && (
                    <DialogContentText sx={{ my: 2 }}>
                        {strings("messages.exclusionReasonSelection", {
                            count: selectedAssets.length
                        })}
                    </DialogContentText>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={6} md={6}>
                        <FormControl>
                            <RadioGroup
                                aria-labelledby="demo-controlled-radio-buttons-group"
                                name="controlled-radio-buttons-group"
                                value={exclusionReason}
                                onChange={onChange("exclusionReason")}
                            >
                                {exclusionReasonTypes.data.map((item) => {
                                    return (
                                        <FormControlLabel
                                            key={`${item.id}_${item.name}`}
                                            value={item.id}
                                            control={<Radio size="small" />}
                                            label={
                                                <Typography
                                                    variant="caption"
                                                    component="span"
                                                >
                                                    {item.name}
                                                </Typography>
                                            }
                                        />
                                    );
                                })}
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={6}>
                        {/* Date Picker  for exclusion reason (Asset is sold & not paid) */}
                        {isExclusionDatePicker && (
                            <>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                >
                                    <DatePicker
                                        disableFuture={
                                            assetIsSold ===
                                            parseInt(exclusionReason)
                                        }
                                        disabled={disableDatePicker}
                                        label={
                                            assetIsSold ===
                                            parseInt(exclusionReason)
                                                ? strings("soldDate")
                                                : strings("dateOfPayment")
                                        }
                                        value={selectedDate} // always a Dayjs object or null
                                        onChange={(val) => setSelectedDate(val)}
                                        slotProps={{
                                            textField: { fullWidth: true }
                                        }}
                                    />
                                </LocalizationProvider>

                                <Divider sx={styles.spacer} />
                            </>
                        )}

                        {parseInt(exclusionReason) ===
                            assetDeliveredToCustomerAndNotPaid && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={dateNotRequired}
                                        onChange={onDisableDateCheckboxChange}
                                    />
                                }
                                label={strings("messages.dateUnavailable")}
                            />
                        )}

                        {activeItem === "enterList" && (
                            <>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    multiline
                                    onChange={onChange("identifiers")}
                                    placeholder={`${strings(
                                        "messages.enterList"
                                    )}`}
                                    minRows={8}
                                    maxRows={10}
                                    value={identifiers}
                                    variant="outlined"
                                />
                                <Divider sx={styles.spacer} />
                                {/* Displaying (Valid/Invlaid) vin lengths */}
                                {identifiers.length > 0 &&
                                    Object.keys(validList).map((item) => (
                                        <ListItem key={item} sx={{ py: 0 }}>
                                            <ListItemText
                                                secondary={`${strings(
                                                    item
                                                )} - ${validList[item]}`}
                                            />
                                        </ListItem>
                                    ))}
                                <Divider sx={styles.spacer} />
                            </>
                        )}

                        <TextField
                            fullWidth
                            margin="normal"
                            multiline
                            onChange={onChange("note")}
                            placeholder={strings("messages.enterNote")}
                            minRows={4}
                            value={note}
                            variant="outlined"
                        />
                        <Divider sx={styles.spacer} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ m: 2 }}>
                <Button
                    variant="contained"
                    sx={styles.primaryButton}
                    onClick={onSubmit}
                    disabled={isDisabled}
                >
                    {strings("submit")}
                </Button>
            </DialogActions>
        </>
    );
};
